import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePlan } from '@/hooks/usePlan';
import { ConfirmDialog } from './ConfirmDialog';
import { UpgradeModal } from './UpgradeModal';
import {
  Eye, EyeSlash, ArrowUp, ArrowDown, PencilSimple, Trash, Link as LinkIcon, Image,
} from '@phosphor-icons/react';
import { CmsProjectsSkeleton } from './CmsSkeletons';

interface Project {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  sort_order: number;
  published: boolean;
  layout_type: string;
}

interface AdminProjectsProps {
  sectionSlug: string;
  sectionName: string;
  onSelectProject: (projectId: string) => void;
  onDataChange?: () => void;
}

function slugify(text: string): string {
  return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

const monoFont = "'IBM Plex Mono', monospace";
const C = {
  fg: 'rgba(247,246,242,0.85)',
  fgMuted: 'rgba(247,246,242,0.45)',
  border: 'rgba(247,246,242,0.08)',
  borderStrong: 'rgba(247,246,242,0.25)',
  bgHover: 'rgba(247,246,242,0.06)',
  destructive: '#FF3F2D',
};

export function AdminProjects({ sectionSlug, sectionName, onSelectProject, onDataChange }: AdminProjectsProps) {
  const { session } = useAuth();
  const { isFree, limits } = usePlan();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [totalGalleries, setTotalGalleries] = useState(0);

  const fetchProjects = async () => {
    if (!session) return;
    const { data } = await supabase.from('projects').select('*').eq('section_slug', sectionSlug).eq('user_id', session.user.id).order('sort_order');
    setProjects((data || []) as Project[]);
    // Count total galleries across all sections
    const { count } = await supabase.from('projects').select('id', { count: 'exact', head: true }).eq('user_id', session.user.id);
    setTotalGalleries(count || 0);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    setEditingId(null);
    setShowAdd(false);
    fetchProjects();
  }, [sectionSlug]);

  const addProject = async () => {
    if (!newName.trim() || !session) return;
    // Check gallery limit
    if (limits.maxGalleries !== null && totalGalleries >= limits.maxGalleries) {
      setShowUpgrade(true);
      return;
    }
    await supabase.from('projects').insert({
      user_id: session.user.id,
      section_slug: sectionSlug, name: newName.trim(), slug: slugify(newName),
      description: newDesc.trim() || null, layout_type: 'A', sort_order: projects.length,
    } as any);
    setNewName(''); setNewDesc(''); setShowAdd(false);
    fetchProjects(); onDataChange?.();
  };

  const deleteProject = async (id: string) => {
    await supabase.from('projects').delete().eq('id', id);
    fetchProjects(); onDataChange?.();
  };

  const updateProject = async (id: string, name: string, description: string | null) => {
    await supabase.from('projects').update({ name, description, slug: slugify(name) }).eq('id', id);
    setEditingId(null); fetchProjects(); onDataChange?.();
  };

  const togglePublish = async (project: Project) => {
    await supabase.from('projects').update({ published: !project.published }).eq('id', project.id);
    fetchProjects(); onDataChange?.();
  };

  const moveProject = async (index: number, dir: 'up' | 'down') => {
    const swapIndex = dir === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= projects.length) return;
    const a = projects[index]; const b = projects[swapIndex];
    await supabase.from('projects').update({ sort_order: b.sort_order }).eq('id', a.id);
    await supabase.from('projects').update({ sort_order: a.sort_order }).eq('id', b.id);
    fetchProjects();
  };

  const inputStyle: React.CSSProperties = {
    fontFamily: monoFont, fontSize: 13, fontWeight: 300, padding: '6px 10px',
    border: `1px solid rgba(247,246,242,0.15)`, backgroundColor: 'rgba(247,246,242,0.04)', color: C.fg, width: '100%',
  };

  if (loading) return <CmsProjectsSkeleton />;

  return (
    <div>
      <UpgradeModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        title="Limite de galerias atingido"
        description={`No plano grátis você pode ter até ${limits.maxGalleries} galerias. Faça upgrade para o Pro e tenha galerias ilimitadas.`}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Remover projeto"
        description="Deseja remover este projeto e todas as suas imagens? Esta ação não pode ser desfeita."
        confirmLabel="Remover" cancelLabel="Cancelar" destructive
        onConfirm={() => { if (deleteTarget) deleteProject(deleteTarget); }}
      />

      <div className="flex items-center justify-between mb-6">
        <h2 className="uppercase text-sm font-light tracking-wider" style={{ color: C.fg }}>{sectionName}</h2>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="uppercase text-xs py-1 px-3"
          style={{ border: `1px solid ${C.borderStrong}`, fontFamily: monoFont, color: C.fg, backgroundColor: 'transparent', cursor: 'pointer', transition: 'background-color 0.15s ease' }}
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = C.bgHover)}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
        >
          {showAdd ? 'CANCELAR' : '+ PROJETO'}
        </button>
      </div>

      {showAdd && (
        <div className="mb-6 p-4 flex flex-col gap-3" style={{ border: `1px dashed rgba(247,246,242,0.15)` }}>
          <input placeholder="Nome do projeto" value={newName} onChange={(e) => setNewName(e.target.value)} style={inputStyle} />
          <textarea placeholder="Descrição (opcional)" value={newDesc} onChange={(e) => setNewDesc(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
          <button onClick={addProject} disabled={!newName.trim()} className="uppercase text-xs py-2 px-4 disabled:opacity-30 self-start"
            style={{ border: `1px solid ${C.borderStrong}`, fontFamily: monoFont, color: C.fg, backgroundColor: 'transparent', cursor: 'pointer' }}
          >
            CRIAR PROJETO
          </button>
        </div>
      )}

      <div className="flex flex-col gap-4">
        {projects.map((project, i) => (
          <div key={project.id} style={{ border: `1px solid ${C.border}`, padding: 24 }}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                {editingId === project.id ? (
                  <EditProjectForm project={project} onSave={(name, desc) => updateProject(project.id, name, desc)} onCancel={() => setEditingId(null)} inputStyle={inputStyle} />
                ) : (
                  <>
                    <button
                      onClick={() => onSelectProject(project.id)}
                      style={{ fontFamily: monoFont, fontSize: 16, fontWeight: 300, color: '#F7F6F2', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left', transition: 'opacity 0.15s ease' }}
                      onMouseEnter={e => { e.currentTarget.style.opacity = '0.7'; }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                    >
                      {project.name}
                    </button>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-[10px] uppercase px-2 py-0.5" style={{
                        fontFamily: monoFont,
                        border: '0.5px solid',
                        borderColor: project.published ? 'hsl(142 71% 45%)' : 'rgba(247,246,242,0.15)',
                        color: project.published ? 'hsl(142 71% 45%)' : C.fgMuted,
                      }}>
                        {project.published ? 'Publicado' : 'Rascunho'}
                      </span>
                    </div>
                    {project.description && <p className="text-xs mt-2 whitespace-pre-wrap" style={{ color: C.fgMuted }}>{project.description}</p>}
                  </>
                )}
              </div>

              {editingId !== project.id && (
                <div className="flex items-center gap-3 shrink-0">
                  <CmsIconButton icon={<Image size={14} weight="thin" />} label="FOTOS" onClick={() => onSelectProject(project.id)} />
                  <CmsIconButton
                    icon={project.published ? <EyeSlash size={14} weight="thin" /> : <Eye size={14} weight="thin" />}
                    label={project.published ? 'DESPUBLICAR' : 'PUBLICAR'}
                    onClick={() => togglePublish(project)}
                  />
                  <CmsIconButton icon={<PencilSimple size={14} weight="thin" />} label="EDITAR" onClick={() => setEditingId(project.id)} />
                  <CmsTextButton label="↑" onClick={() => moveProject(i, 'up')} disabled={i === 0} />
                  <CmsTextButton label="↓" onClick={() => moveProject(i, 'down')} disabled={i === projects.length - 1} />
                  <CmsIconButton icon={<Trash size={14} weight="thin" />} label="APAGAR" onClick={() => setDeleteTarget(project.id)} destructive />
                </div>
              )}
            </div>
          </div>
        ))}
        {projects.length === 0 && (
          <p className="text-xs uppercase" style={{ fontFamily: monoFont, color: C.fgMuted }}>Nenhum projeto ainda. Clique em "+ PROJETO" para começar.</p>
        )}
      </div>
    </div>
  );
}

function CmsTextButton({ label, onClick, destructive, disabled }: {
  label: string; onClick: () => void; destructive?: boolean; disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="uppercase disabled:opacity-20"
      style={{
        fontFamily: monoFont, fontSize: 12, fontWeight: 300,
        color: destructive ? '#FF3F2D' : 'rgba(247,246,242,0.6)',
        background: 'none', border: 'none',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'color 0.15s ease',
        opacity: destructive ? 0.7 : 1,
      }}
      onMouseEnter={e => {
        if (!disabled) e.currentTarget.style.color = destructive ? '#FF3F2D' : '#F7F6F2';
        if (destructive && !disabled) e.currentTarget.style.opacity = '1';
      }}
      onMouseLeave={e => {
        if (!disabled) e.currentTarget.style.color = destructive ? '#FF3F2D' : 'rgba(247,246,242,0.6)';
        if (destructive && !disabled) e.currentTarget.style.opacity = '0.7';
      }}
    >
      {label}
    </button>
  );
}

function CmsIconButton({ icon, label, onClick, destructive, disabled }: {
  icon: React.ReactNode; label: string; onClick: () => void; destructive?: boolean; disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="uppercase disabled:opacity-20 flex items-center gap-1"
      style={{
        fontFamily: monoFont, fontSize: 12, fontWeight: 300,
        color: destructive ? '#FF3F2D' : 'rgba(247,246,242,0.6)',
        background: 'none', border: 'none',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'color 0.15s ease',
        opacity: destructive ? 0.7 : 1,
      }}
      onMouseEnter={e => {
        if (!disabled) e.currentTarget.style.color = destructive ? '#FF3F2D' : '#F7F6F2';
        if (destructive && !disabled) e.currentTarget.style.opacity = '1';
      }}
      onMouseLeave={e => {
        if (!disabled) e.currentTarget.style.color = destructive ? '#FF3F2D' : 'rgba(247,246,242,0.6)';
        if (destructive && !disabled) e.currentTarget.style.opacity = '0.7';
      }}
    >
      {icon}
      {label}
    </button>
  );
}

function EditProjectForm({ project, onSave, onCancel, inputStyle }: {
  project: { name: string; description: string | null }; onSave: (name: string, desc: string | null) => void; onCancel: () => void; inputStyle: React.CSSProperties;
}) {
  const [name, setName] = useState(project.name);
  const [desc, setDesc] = useState(project.description || '');

  return (
    <div className="flex flex-col gap-2">
      <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
      <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
      <div className="flex gap-2">
        <button onClick={() => onSave(name, desc.trim() || null)} className="uppercase text-xs py-1 px-3"
          style={{ border: `1px solid rgba(247,246,242,0.25)`, fontFamily: monoFont, color: 'rgba(247,246,242,0.85)', backgroundColor: 'transparent', cursor: 'pointer' }}>SALVAR</button>
        <button onClick={onCancel} className="uppercase text-xs py-1 px-3"
          style={{ fontFamily: monoFont, color: C.fgMuted, backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}>CANCELAR</button>
      </div>
    </div>
  );
}
